import { FeatureAppDefinition } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import { ThemeProvider } from '@vw-marketing/us-components';
import React from 'react';
import { styled } from '@vw-marketing/us-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { featureAppMemoryStoreDefinition } from './shared/hooks/feature-app-memory-store';
import { FeatureAppEnvironmentContext } from './shared/hooks/use-feature-app-environment';
import { FeatureServicesContext } from './shared/hooks/use-feature-services';
import { App } from './app/App';
import {
  FeatureAppConfig,
  FeatureAppDependencies,
} from './shared/typings/main';
import FeatureAppService from './shared/services/FeatureAppServices';
import { setFeatureAppConfigInSessionStorage } from '@vw-marketing/myvw-portal-web-utils/Utils/Helpers/Helper';
import { Router } from 'react-router-dom';
import { LaunchDarklyWrapper } from './shared/components/launch-darkly-wrapper/LaunchDarklyWrapper';
declare var __VERSION__: String;

export const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  FeatureAppDependencies,
  FeatureAppConfig
> = {
  dependencies: {
    featureServices: {
      's2:history': '^2.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'feature-app-memory-store': '^1.0.0',
      'featureapp-service': '^1.3.0',
      'locale-service': '^1.0.0',
      'service-config-provider': '^1.0.0',
      'myvw:myvw-auth-feature-app-service': '^1.0.0',
      'navigation-service': '^1.1.0',
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:async-ssr-manager': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:server-request': '^1.0.0',
      'tracking': '^1.0.0',
      'fa-content-service': '^1.0.0',
      'layer-manager': '^2.0.0',
      'disclaimer-manager': '^1.2.0',
    },
  },
  ownFeatureServiceDefinitions: [
    featureAppMemoryStoreDefinition,
    FeatureAppService,
  ],

  create: (env) => {
    setFeatureAppConfigInSessionStorage(env);
    const { featureServices, baseUrl } = env;
    const {
      's2:history': historyService,
      's2:serialized-state-manager': serializedStateManager,
      'feature-app-memory-store': memoryStore,
      'myvw:myvw-auth-feature-app-service': authService,
    } = featureServices;

    const history = historyService.history;

    const serializedState = serializedStateManager.getSerializedState();

    if (serializedState && !memoryStore.getState()) {
      memoryStore.setState(JSON.parse(serializedState));
    }

    serializedStateManager.register(() =>
      JSON.stringify(memoryStore.getState()),
    );

    const StyledVersionContainer: any = styled.div`
      display: none;
    `;

    const ThemedApp = () => {
      const { ciGuidelinesRollout } = useFlags();

      return (
        <ThemeProvider theme="main" variant={ciGuidelinesRollout ? 'v26' : undefined}>
          <StyledVersionContainer>
            Feature App Version :: My VW :: {__VERSION__}
          </StyledVersionContainer>
          <App service={authService as any} baseUrl={baseUrl} />
        </ThemeProvider>
      );
    };

    return {
      render: () => (
        <FeatureAppEnvironmentContext.Provider value={env}>
          <FeatureServicesContext.Provider value={featureServices}>
            <Router history={history}>
              <LaunchDarklyWrapper baseUrl={baseUrl}>
                <ThemedApp />
              </LaunchDarklyWrapper>
            </Router>
          </FeatureServicesContext.Provider>
        </FeatureAppEnvironmentContext.Provider>
      ),
    };
  },
};

export default featureAppDefinition;
